'use strict';

import { validateField } from 'app_vodafone_ziggo/util/validation';

/**
 * @class VZCopy
 */
export default class VZCopy extends HTMLElement {
    static get observedAttributes() {
        return ['enabled'];
    }

    constructor() {
        super();
        this.mapping = JSON.parse(this.dataset.mapping);
        this.enabled = this.getAttribute('enabled') === 'true';
        this.copyWhenChecked = this.dataset.copyWhenChecked === 'true';
        this.idToHide = this.dataset.idToHide;

        // This property is necessary because `attributeChangedCallback` is called before
        // `connectedCallback` which results in unwanted behavior
        this.enableComponent = false;

        if (this.enabled) {
            this.syncInput = this.syncInput.bind(this);

            /**
             * Since we are blocking fn copyValue from being called on load with this.enableComponent, the readonly attribute is not set.
             * This causes issues where the form is marked as invalid, so it needs to be set.
             */
            this.getFields().forEach((field) => this.copyAndSync(field));
        }
    }

    connectedCallback() {
        this.querySelector('.js-copy').addEventListener('change', (evt) => {
            this.setAttribute('enabled', this.copyWhenChecked ? evt.target.checked : !evt.target.checked);
        });

        this.enableComponent = true;
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.enableComponent) return;
        if (name === 'enabled' && newValue === 'true') this.enableCopy();
        if (name === 'enabled' && newValue === 'false') this.disableCopy();
    }

    getFields() {
        return Object.keys(this.mapping)
            .map((field) => {
                return {
                    to: document.querySelector(`[name="${this.mapping[field]}"]`),
                    from: document.querySelector(`[name="${field}"]`)
                };
            })
            .filter((field) => field.to && field.from);
    }

    copyAndSync(field) {
        this.copyValue(field.to, field.from);
        field.from.addEventListener('input', this.syncInput.bind(this));
    }

    static setOrUnsetAttributes(toField, shouldBeRemoved) {
        if (shouldBeRemoved) {
            toField.removeAttribute('readonly');
            toField.removeAttribute('tabindex');
        } else {
            toField.setAttribute('readonly', 'readonly');
            toField.setAttribute('tabindex', '-1');
        }
    }

    copyValue(to, from) {
        if (!to || !from) return;
        to.value = from.value;
        this.constructor.setOrUnsetAttributes(to, false);
        if (from.value.length > 0) to.classList.remove('empty');
    }

    storeOriginalValue() {
        this.getFields().forEach((field) => {
            if (field.to.value) {
                field.to.dataset.originalValue = field.to.value;
            }
        });
    }

    enableCopy() {
        this.storeOriginalValue();
        this.getFields().forEach((field) => this.copyAndSync(field));
        document.getElementById(this.idToHide).setAttribute('visible', 'false');
    }

    disableCopy() {
        this.getFields().forEach((field) => {
            this.constructor.setOrUnsetAttributes(field.to, true);
            field.to.value = field.to.dataset.originalValue || '';
            if (field.to.value.length === 0) field.to.classList.add('empty');
            field.from.removeEventListener('input', this.syncInput);

            // Preselect an option in a select field when there's one enabled option
            if (field.to.tagName === 'SELECT') {
                const enabledOptions = Array.from(field.to.options).filter((option) => !option.disabled);
                if (enabledOptions.length === 1) {
                    field.to.value = enabledOptions[0].value;
                    validateField(field.to);
                }
            }
        });
        document.getElementById(this.idToHide).setAttribute('visible', 'true');
    }

    syncInput(evt) {
        const {
            target: { name, value, type }
        } = evt;
        let to;

        if (type === ('radio' || 'checkbox')) {
            to = document.querySelector(`[name="${this.mapping[name]}"][value="${value}"]`);
        } else {
            to = document.querySelector(`[name="${this.mapping[name]}"]`);
        }
        this.copyValue(to, evt.target);
    }
}

if (!window.customElements.get('vz-copy')) {
    window.customElements.define('vz-copy', VZCopy);
}
